/**
 * Customized version of react-select's Creatable.
 * Will only be used to add tags and aliases; Hence not a common component
 */

import {
  hexToRGB,
  MOON_100,
  MOON_150,
  MOON_250,
  MOON_350,
  MOON_500,
  MOON_800,
  PURPLE_400,
  PURPLE_550,
  RED_500,
  SIENNA_300,
  SIENNA_600,
  TEAL_300,
  TEAL_500,
  TEAL_600,
} from '@wandb/weave/common/css/globals.styles';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
import React from 'react';
import {
  components,
  DropdownIndicatorProps,
  GroupBase,
  MultiValueGenericProps,
  MultiValueRemoveProps,
  Props,
  StylesConfig,
} from 'react-select';
import Creatable from 'react-select/creatable';

import {TagType} from '../../../Tags';

export const SelectSizes = {
  Small: 'small',
  Medium: 'medium',
  Large: 'large',
} as const;
export type SelectSize = (typeof SelectSizes)[keyof typeof SelectSizes];

const HEIGHTS: Record<SelectSize, number> = {
  small: 24,
  medium: 32,
  large: 40,
} as const;

const LINE_HEIGHTS: Record<SelectSize, string> = {
  small: '20px',
  medium: '24px',
  large: '24px',
} as const;

const FONT_SIZES: Record<SelectSize, string> = {
  small: '14px',
  medium: '16px',
  large: '16px',
} as const;

const PADDING: Record<SelectSize, string> = {
  small: '2px 8px',
  medium: '4px 12px',
  large: '8px 12px',
} as const;

const LABEL_COLOR: Record<TagType, string> = {
  [TagType.TAG]: 'teal',
  [TagType.ALIAS]: 'sienna',
  [TagType.PROTECTED_ALIAS]: 'purple',
};

const BACKGROUND_COLOR: Record<string, string> = {
  teal: `${hexToRGB(TEAL_300, 0.48)}`,
  sienna: `${hexToRGB(SIENNA_300, 0.48)}`,
  purple: `${PURPLE_400}`,
};

const FONT_COLOR: Record<string, string> = {
  teal: `${TEAL_600}`,
  sienna: `${SIENNA_600}`,
  purple: `${PURPLE_550}`,
};

interface AdditionalProps {
  size?: SelectSize;
  tagType?: TagType;
  errorState?: boolean;
}

// Toggle icon when open
const DropdownIndicator = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: DropdownIndicatorProps<Option, IsMulti, Group>
) => {
  const iconName = props.selectProps.menuIsOpen ? 'chevron-up' : 'chevron-down';
  return (
    <components.DropdownIndicator {...props}>
      <Icon name={iconName} width={16} height={16} color={MOON_500} />
    </components.DropdownIndicator>
  );
};

// Add Icon based on TagType
const MultiValueLabelWithIcon = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: MultiValueGenericProps<Option, IsMulti, Group> & AdditionalProps
) => {
  const size = props.size ?? 'small';
  const tagType = props.tagType ?? TagType.TAG;
  return (
    <components.MultiValueLabel {...props}>
      {props.tagType !== undefined && (
        <Icon
          name={tagType === TagType.TAG ? 'tag' : 'email-at'}
          size={size}
          style={{marginRight: '6px', marginLeft: '-6px'}}
        />
      )}
      {props.children}
    </components.MultiValueLabel>
  );
};

const MultiValueRemoveWithIcon = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: MultiValueRemoveProps<Option, IsMulti, Group> & AdditionalProps
) => {
  const size = props.size ?? 'small';
  return (
    <components.MultiValueRemove {...props}>
      <Icon name={'close'} size={size} />
      {props.children}
    </components.MultiValueRemove>
  );
};

// See: https://react-select.com/typescript
export const CreatableSelect = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: Props<Option, IsMulti, Group> & AdditionalProps
) => {
  const tagType = props.tagType ?? TagType.TAG;

  // Override styling to come closer to design spec.
  // See: https://react-select.com/home#custom-styles
  // See: https://github.com/JedWatson/react-select/issues/2728
  const size = props.size ?? 'small';
  const styles: StylesConfig<Option, IsMulti, Group> = {
    // No vertical line to left of dropdown indicator
    indicatorSeparator: baseStyles => ({...baseStyles, display: 'none'}),
    input: baseStyles => {
      return {
        ...baseStyles,
        padding: 0,
      };
    },
    valueContainer: baseStyles => {
      if (!props.isMulti && props.tagType == null) {
        return {...baseStyles};
      }
      const padding = PADDING[size];
      return {...baseStyles, padding};
    },
    indicatorsContainer: baseStyles => {
      if (!props.isMulti && props.tagType == null) {
        return {...baseStyles};
      }
      const padding = PADDING[size];
      return {
        ...baseStyles,
        marginBottom: 'auto',
        padding,
      };
    },
    container: baseStyles => {
      const height = HEIGHTS[size];
      return {
        ...baseStyles,
        height,
        display: 'inline',
      };
    },
    control: (baseStyles, state) => {
      const colorBorderDefault = MOON_250;
      const colorBorderHover = hexToRGB(TEAL_500, 0.4);
      const colorBorderOpen = hexToRGB(TEAL_500, 0.64);
      const height = HEIGHTS[size];
      const lineHeight = LINE_HEIGHTS[size];
      const fontSize = FONT_SIZES[size];
      return {
        ...baseStyles,
        height: 'auto',
        minHeight: height,
        lineHeight,
        fontSize,
        border: 0,
        boxShadow: state.menuIsOpen
          ? `0 0 0 2px ${colorBorderOpen}`
          : state.isFocused
            ? `0 0 0 2px ${props.errorState ? RED_500 : colorBorderHover}`
            : `inset 0 0 0 1px ${colorBorderDefault}`,
        '&:hover': {
          boxShadow: state.menuIsOpen
            ? `0 0 0 2px ${colorBorderOpen}`
            : `0 0 0 2px ${colorBorderHover}`,
        },
      };
    },
    placeholder: (baseStyles, state) => {
      return {
        ...baseStyles,
        fontSize: '16px',
        lineHeight: '24px',
      };
    },
    groupHeading: (baseStyles, state) => {
      return {
        ...baseStyles,
        fontSize: '16px',
        fontWeight: 600,
        color: MOON_800,
        textTransform: 'none',
      };
    },
    option: (baseStyles, state) => {
      return {
        ...baseStyles,
        cursor: state.isDisabled ? 'default' : 'pointer',
        // TODO: Should icon be translucent?
        color: state.isDisabled ? MOON_350 : MOON_800,
        height: '36px',
        padding: '6px 12px',
        backgroundColor: state.isDisabled
          ? undefined
          : state.isSelected
            ? MOON_150
            : state.isFocused
              ? MOON_100
              : undefined,
        ':active': {
          // mousedown
          ...baseStyles[':active'],
          backgroundColor: !state.isDisabled ? MOON_150 : undefined,
        },
      };
    },
    multiValue: baseStyles => {
      const padding = PADDING[size];
      const height = HEIGHTS[size];
      const lineHeight = LINE_HEIGHTS[size];
      const fontSize = FONT_SIZES[size];
      const color = LABEL_COLOR[tagType];
      const background = BACKGROUND_COLOR[color];
      const fontColor = FONT_COLOR[color];
      return {
        ...baseStyles,
        borderRadius: '4px',
        padding,
        height,
        lineHeight,
        fontSize,
        margin: '2px 4px 2px 0',
        alignItems: 'center',
        backgroundColor: background,
        color: fontColor,
      };
    },
    multiValueLabel: baseStyles => {
      const height = HEIGHTS[size];
      const lineHeight = LINE_HEIGHTS[size];
      const fontSize = FONT_SIZES[size];
      const color = LABEL_COLOR[tagType];
      const fontColor = FONT_COLOR[color];
      return {
        ...baseStyles,
        borderRadius: '4px',
        height,
        lineHeight,
        fontSize,
        margin: '0',
        display: 'flex',
        alignItems: 'center',
        color: fontColor,
      };
    },
    multiValueRemove: baseStyles => {
      return {
        ...baseStyles,
        padding: '0',
        margin: '0',
        display: 'flex',
        alignItems: 'center',
        '&:hover': {
          color: 'red',
          background: 'none',
        },
      };
    },
  };
  return (
    <Creatable
      noOptionsMessage={() => null}
      components={{
        DropdownIndicator,
        MultiValueLabel: MultiValueLabelWithIcon,
        MultiValueRemove: MultiValueRemoveWithIcon,
      }}
      styles={styles}
      {...props}
    />
  );
};
